/* src/contexts/ProfileContext.js */
import React, {
    useState,
    useEffect,
    useCallback,
    useMemo,
    useRef
} from 'react';
import { doc, updateDoc, Timestamp, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { db as firestoreDb } from '../firebaseConfig'; // Import db instance

// Existing Internal Imports
import { ProfileContext, useProfile } from './profile/profileContextSetup';
import { useAuthStateListener } from './profile/profileAuth';
// *** We are removing useProfileListLoader and handling its logic inside this context ***
import { useSubcollectionListeners } from './profile/profileDataFetching';
import { sanitizeTag } from './profile/profileUtils';
import { LOCAL_STORAGE_KEY, DEFAULT_PROFILE_DATA } from './profile/profileConstants';

// Cloud Function Imports (Used by action creators)
import {
    getUserDataOnLoginFn, // <<< IMPORT THE NEW FUNCTION
    deleteProfileAndCleanupFn, sendFriendRequestFn, acceptFriendRequestFn,
    handleFriendRequestFn, removeFriendFn, searchUsersFn,
    setProfileUsernameFn, checkSubscriptionStatusFn,
    fetchUserSessionsFn, exportUserSessionsFn
} from './profile/profileCloudFunctions';

// Action Hook Creator Imports
import {
    useCreateAddProfileAction,
    useCreateSetProfileUsernameAction,
    useCreateUpdateProfileDataAction,
    useCreateUpdateProfileProgressAction,
    useCreateRemoveProfileAction,
    useCreateMarkTutorialCompletedAction
} from './profile/profileActionsCRUD';
import {
    useCreateAddJournalTagAction,
    useCreateAddJournalEntryAction,
    useCreateUpdateJournalEntryAction,
    useCreateRemoveJournalEntryAction
} from './profile/profileActionsJournal';
import { useCreateAddCstResultAction } from './profile/profileActionsCST';
import {
    useCreateSendFriendRequestAction,
    useCreateAcceptFriendRequestAction,
    useCreateRejectFriendRequestAction,
    useCreateCancelFriendRequestAction,
    useCreateRemoveFriendAction,
    useCreateSearchUsersAction
} from './profile/profileActionsFriends';
import {
    useCreateFetchPaginatedSessionsAction,
    useCreateFetchAllSessionsForExportAction
} from './profile/profileActionsSessions';
import { useCreateCheckSubscriptionAction } from './profile/profileActionsSubscription';
import { useCreateMarkOnboardingCompleteAction } from './profile/profileActionsOnboarding';


// Re-export useProfile hook for convenience
export { useProfile };

const getDefaultProfileStructure = () => ({
    ...DEFAULT_PROFILE_DATA,
    id: null,
    username: '',
    highestNBackGood: null,
    highestAchievedGoodSessionId: null,
});

export const ProfileProvider = ({ children }) => {
    // --- State Definitions ---
    const [profiles, setProfiles] = useState([]);
    const [activeProfileIdState, setActiveProfileIdState] = useState(null);
    const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false); // New primary loading flag

    const [activeSessions, setActiveSessions] = useState([]);
    const [activeJournal, setActiveJournal] = useState([]);
    const [activeFriends, setActiveFriends] = useState([]);
    const [activeRequestsReceived, setActiveRequestsReceived] = useState([]);
    const [activeRequestsSent, setActiveRequestsSent] = useState([]);
    const [error, setError] = useState(null);
    const [userSubscriptionStatus, setUserSubscriptionStatus] = useState(null);
    const [userSubscriptionEndDate, setUserSubscriptionEndDate] = useState(null);
    const [userSubscriptionPriceId, setUserSubscriptionPriceId] = useState(null);
    const [isOnboardingComplete, setIsOnboardingComplete] = useState(false);
    const [userHadTrial, setUserHadTrial] = useState(false);
    
    const { authUser, isAuthResolved } = useAuthStateListener();
    const authUserRef = useRef(authUser);
    useEffect(() => { authUserRef.current = authUser; }, [authUser]);
    const userId = authUser?.uid || null;

    // Subcollection listener now depends on isInitialLoadComplete
    const {
        activeSessions: loadedSessions,
        activeJournal: loadedJournal,
        activeFriends: loadedFriends,
        activeRequestsReceived: loadedReceived,
        activeRequestsSent: loadedSent,
        isLoadingActiveSubcollections,
        subcollectionError: profileSubcollectionError,
    } = useSubcollectionListeners(userId, activeProfileIdState, !isInitialLoadComplete);

    // --- Actions ---
    const selectProfile = useCallback(async (profileId) => {
        if (profileId === activeProfileIdState) return;
        setActiveProfileIdState(profileId);
        try {
            if (profileId) localStorage.setItem(LOCAL_STORAGE_KEY, profileId);
            else localStorage.removeItem(LOCAL_STORAGE_KEY);
        } catch (e) {}
        if (authUserRef.current?.uid) {
            try {
                await updateDoc(doc(firestoreDb, 'users', authUserRef.current.uid), {
                    activeProfileId: profileId,
                    updatedAt: serverTimestamp()
                });
            } catch (e) {
                console.error("Failed to save active profile preference.", e);
                setError("Failed to save active profile preference.");
            }
        }
    }, [activeProfileIdState]);

    // --- All other action hooks remain unchanged ---
    const markOnboardingComplete = useCreateMarkOnboardingCompleteAction(setIsOnboardingComplete);
    const addProfile = useCreateAddProfileAction(authUserRef, profiles, setError, setProfileUsernameFn);
    const setProfileUsername = useCreateSetProfileUsernameAction(authUserRef, setError, setProfileUsernameFn);
    const updateProfileData = useCreateUpdateProfileDataAction(authUserRef, setProfiles, setError);
    const updateProfileProgress = useCreateUpdateProfileProgressAction(authUserRef, setProfiles, setError);
    const markTutorialCompleted = useCreateMarkTutorialCompletedAction(authUserRef, setProfiles, setError);
    const removeProfile = useCreateRemoveProfileAction(authUserRef, activeProfileIdState, profiles, selectProfile, setError, deleteProfileAndCleanupFn);
    const addJournalTag = useCreateAddJournalTagAction(authUserRef, profiles, updateProfileData, setError);
    const addJournalEntry = useCreateAddJournalEntryAction(authUserRef, profiles, updateProfileData, setError);
    const updateJournalEntry = useCreateUpdateJournalEntryAction(authUserRef, profiles, updateProfileData, setError);
    const removeJournalEntry = useCreateRemoveJournalEntryAction(authUserRef, setError);
    const addCstResult = useCreateAddCstResultAction(authUserRef, setError);
    const sendFriendRequest = useCreateSendFriendRequestAction(authUserRef, activeProfileIdState, setError, sendFriendRequestFn);
    const acceptFriendRequest = useCreateAcceptFriendRequestAction(authUserRef, activeProfileIdState, setError, acceptFriendRequestFn);
    const rejectFriendRequest = useCreateRejectFriendRequestAction(authUserRef, activeProfileIdState, setError, handleFriendRequestFn);
    const cancelFriendRequest = useCreateCancelFriendRequestAction(authUserRef, activeProfileIdState, setError, handleFriendRequestFn);
    const removeFriend = useCreateRemoveFriendAction(authUserRef, activeProfileIdState, setError, removeFriendFn);
    const searchUsers = useCreateSearchUsersAction(authUserRef, activeProfileIdState, setError, searchUsersFn);
    const fetchPaginatedSessions = useCreateFetchPaginatedSessionsAction(authUserRef, fetchUserSessionsFn);
    const fetchAllSessionsForExport = useCreateFetchAllSessionsForExportAction(authUserRef, exportUserSessionsFn);
    const checkSubscription = useCreateCheckSubscriptionAction(
        authUserRef, () => {}, setUserSubscriptionStatus,
        setUserSubscriptionEndDate, setUserSubscriptionPriceId, () => {},
        checkSubscriptionStatusFn
    );

    // --- New, Centralized Data Loading Logic ---
    useEffect(() => {
        const loadInitialData = async () => {
            setIsInitialLoadComplete(false); // Start loading

            if (!userId) {
                // No user, so loading is complete. Reset state.
                setProfiles([]);
                setActiveProfileIdState(null);
                setUserSubscriptionStatus(null);
                setIsInitialLoadComplete(true);
                return;
            }

            console.log(`[ProfileContext] User ${userId} detected. Calling getUserDataOnLogin...`);
            try {
                const result = await getUserDataOnLoginFn();
                if (!result?.data?.success) {
                    throw new Error(result?.data?.message || "Backend function returned failure.");
                }
                
                const { userData, profiles: fetchedProfiles } = result.data;
                console.log("[ProfileContext] Received initial data:", { userData, profiles: fetchedProfiles });

                // Set user-level data
                setUserSubscriptionStatus(userData.subscriptionStatus || 'inactive');
                setUserHadTrial(userData.hasHadTrial || false);
                
                // Set the list of profiles
                setProfiles(fetchedProfiles || []);

                // Determine the active profile ID with correct priority
                let newActiveProfileId = null;
                const availableProfileIds = new Set((fetchedProfiles || []).map(p => p.id));

                if (userData.activeProfileId && availableProfileIds.has(userData.activeProfileId)) {
                    // 1. Priority: Use the ID from Firestore user doc
                    newActiveProfileId = userData.activeProfileId;
                } else if ((fetchedProfiles || []).length > 0) {
                    // 2. Priority: Fallback to the first profile in the list
                    newActiveProfileId = fetchedProfiles[0].id;
                }
                
                console.log(`[ProfileContext] Initial load complete. Determined active profile: ${newActiveProfileId}`);
                setActiveProfileIdState(newActiveProfileId);

            } catch (err) {
                console.error("[ProfileContext] CRITICAL: Failed to fetch initial user data:", err);
                setError("Could not load your session. Please refresh the page.");
                setProfiles([]);
                setActiveProfileIdState(null);
                setUserSubscriptionStatus('error');
            } finally {
                setIsInitialLoadComplete(true);
            }
        };

        if (isAuthResolved) {
            loadInitialData();
        }
    }, [isAuthResolved, userId]);

    // Sync subcollection states with loaded data
    useEffect(() => { setActiveSessions(loadedSessions); }, [loadedSessions]);
    useEffect(() => { setActiveJournal(loadedJournal); }, [loadedJournal]);
    useEffect(() => { setActiveFriends(loadedFriends); }, [loadedFriends]);
    useEffect(() => { setActiveRequestsReceived(loadedReceived); }, [loadedReceived]);
    useEffect(() => { setActiveRequestsSent(loadedSent); }, [loadedSent]);
    useEffect(() => { if (profileSubcollectionError) setError(profileSubcollectionError); }, [profileSubcollectionError]);

    // --- isLoading calculation is now much simpler and more reliable ---
    const isLoading = !isAuthResolved || !isInitialLoadComplete;
    
    // --- Derived State (unchanged) ---
    const activeProfileData = useMemo(() => {
        if (!activeProfileIdState || !Array.isArray(profiles) || profiles.length === 0) return getDefaultProfileStructure();
        const foundProfile = profiles.find(p => p.id === activeProfileIdState);
        if (!foundProfile) return getDefaultProfileStructure();
        return { ...getDefaultProfileStructure(), ...foundProfile, id: foundProfile.id };
    }, [activeProfileIdState, profiles]);

    const combinedActiveProfile = useMemo(() => {
         if (!activeProfileData || !activeProfileData.id) return null;
         return {
            ...activeProfileData,
            sessions: activeSessions,
            journal: activeJournal,
            friendsList: activeFriends,
            friendRequestsReceived: activeRequestsReceived,
            friendRequestsSent: activeRequestsSent,
         };
    }, [activeProfileData, activeSessions, activeJournal, activeFriends, activeRequestsReceived, activeRequestsSent]);

    const value = useMemo(() => ({
        authUser,
        isAuthLoading: !isAuthResolved,
        profiles,
        activeProfileId: activeProfileIdState,
        activeProfile: combinedActiveProfile, 
        isLoading,
        error,
        userSubscriptionStatus, userSubscriptionEndDate, userSubscriptionPriceId,
        isSubscriptionLoading: isLoading, // isSubscriptionLoading is now the same as the main isLoading
        subscriptionError: null,
        userHadTrial,
        isOnboardingComplete,
        selectProfile, addProfile, setProfileUsername, removeProfile,
        updateProfileData, updateProfileProgress, markTutorialCompleted, 
        addJournalTag, addJournalEntry, updateJournalEntry, removeJournalEntry,
        addCstResult,
        sendFriendRequest, acceptFriendRequest, rejectFriendRequest, cancelFriendRequest, removeFriend, searchUsers,
        markOnboardingComplete, checkSubscription,
        fetchPaginatedSessions, fetchAllSessionsForExport,
    }), [
        authUser, isAuthResolved, profiles, activeProfileIdState, combinedActiveProfile, 
        isLoading, error, userSubscriptionStatus, userSubscriptionEndDate, userSubscriptionPriceId,
        userHadTrial, isOnboardingComplete,
        selectProfile, addProfile, setProfileUsername, removeProfile,
        updateProfileData, updateProfileProgress, markTutorialCompleted,
        addJournalTag, addJournalEntry, updateJournalEntry, removeJournalEntry,
        addCstResult,
        sendFriendRequest, acceptFriendRequest, rejectFriendRequest, cancelFriendRequest, removeFriend, searchUsers,
        markOnboardingComplete, checkSubscription,
        fetchPaginatedSessions, fetchAllSessionsForExport,
    ]);

    return (
        <ProfileContext.Provider value={value}>
           <React.Fragment key={activeProfileIdState || 'no-profile'}>
                {children}
           </React.Fragment>
        </ProfileContext.Provider>
    );
};